// app/frontend/pages/Articles/Show.tsx
import { useState, useEffect, useMemo, useRef } from "react";
import Layout from "../Layout";
import { Head, Link } from "@inertiajs/react";
import { SiX, SiYcombinator, SiReddit } from "@icons-pack/react-simple-icons";
import { Link as LinkIcon, Check } from "lucide-react";
import { LinkedinIcon } from "@/components/icons/LinkedinIcon";
import { CallToAction } from "@/components/marketing/CallToAction";
import { FAQ, FAQItem } from "@/components/marketing/FAQ";
import { BreadcrumbNavigation } from "@/components/BreadcrumbNavigation";
import { tagPath } from "@/lib/articleTags";
import { useArticleContent } from "@/hooks/useArticleContent";

interface Props {
  article: {
    slug: string;
    title: string;
    excerpt: string;
    content_html: string;
    published_at: string;
    tags: string[];
    featured_image?: string;
    cta_heading?: string;
    cta_subheading?: string;
    faq_title?: string;
    faq_items?: FAQItem[];
  };
}

const PROSE_CLASSNAME =
  "prose prose-lg dark:prose-invert max-w-none prose-img:rounded-xl prose-img:shadow-md prose-img:mx-auto prose-img:max-w-full";

function calculateReadingTime(html: string): number {
  // Remove HTML tags and count words
  const text = html.replace(/<[^>]*>/g, "");
  const wordCount = text.trim().split(/\s+/).length;
  // Assuming average reading speed of 200 words per minute
  return Math.ceil(wordCount / 200);
}

export default function Show({ article }: Props) {
  const readingTime = calculateReadingTime(article.content_html);
  const FEATURED_IMAGE = article.featured_image;
  const NAVBAR_HEIGHT_CLASS = "h-20";

  const parsedContent = useArticleContent(article.content_html);

  // State for image loading
  const [imgLoaded, setImgLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (imgRef.current?.complete) {
      setImgLoaded(true);
    }
  }, []);

  // For copy feedback (optional)
  const [copied, setCopied] = useState(false);

  // Get current URL on client-side only to avoid hydration mismatch
  const [shareUrl, setShareUrl] = useState("");
  const shareTitle = `${article.title} via @infralyst`;

  // Format date on client-side only to avoid hydration mismatch
  const [formattedDate, setFormattedDate] = useState("");

  useEffect(() => {
    setShareUrl(window.location.href);
    setFormattedDate(
      new Date(article.published_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    );
  }, [article.published_at]);

  const blogPostingJsonLd = useMemo(
    () =>
      JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: article.title,
        description: article.excerpt,
        image: article.featured_image,
        datePublished: article.published_at,
        author: {
          "@type": "Organization",
          name: "Infralyst",
          url: "https://infralyst.io",
        },
        publisher: {
          "@type": "Organization",
          name: "Infralyst",
          url: "https://infralyst.io",
          logo: {
            "@type": "ImageObject",
            url: "https://infralyst.io/icon.png",
          },
        },
      }),
    [article.title, article.excerpt, article.featured_image, article.published_at]
  );

  const breadcrumbJsonLd = useMemo(
    () =>
      JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Articles",
            item: "https://infralyst.io/articles",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: article.title,
            item: `https://infralyst.io/articles/${article.slug}`,
          },
        ],
      }),
    [article.title, article.slug]
  );

  return (
    <Layout
      title={article.title}
      description={article.excerpt}
      image={article.featured_image}
      showGradient={false}
    >
      <Head>
        <script type="application/ld+json">{blogPostingJsonLd}</script>
        <script type="application/ld+json">{breadcrumbJsonLd}</script>
      </Head>
      {/* Spacer to push hero below sticky navbar */}
      <div className={NAVBAR_HEIGHT_CLASS} />

      <section className="pb-4">
        <div className="max-w-4xl mx-auto px-4 pt-8 sm:pt-16">
          <BreadcrumbNavigation
            items={[
              { label: "Articles", href: "/articles" },
              { label: article.title },
            ]}
          />
        </div>
        <div className="max-w-3xl mx-auto text-center pb-4 flex flex-col space-y-6">
          {/* Title */}
          <h1 className="mt-2 mb-4 text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight tracking-tight">
            {article.title}
          </h1>
          {/* Excerpt */}
          <div className="max-w-prose mx-auto mb-4">
            <p className="mb-0 text-lg sm:text-xl text-slate-600 dark:text-slate-300 leading-7">
              {article.excerpt}
            </p>
          </div>
          {/* Tags + date/read time */}
          <div className="flex flex-col sm:flex-row justify-center items-center flex-wrap gap-y-2 sm:gap-y-0 gap-x-2 mb-0 sm:mb-4">
            {/* Tags group */}
            <div className="flex gap-3">
              {article.tags &&
                article.tags.length > 0 &&
                article.tags.map((tag: string) => (
                  <Link
                    key={tag}
                    href={tagPath(tag)}
                    className="inline-block rounded-full bg-brand-accent text-white px-4 py-1.5 text-xs font-semibold hover:bg-brand-accent-hover transition-colors"
                  >
                    {tag}
                  </Link>
                ))}
            </div>
            {/* Dot separator only on sm+ */}
            {article.tags && article.tags.length > 0 && (
              <span
                className="text-slate-400 text-xs align-middle hidden sm:inline"
                aria-hidden="true"
              >
                •
              </span>
            )}
            {/* Date and read time */}
            <span className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-2 mt-2 sm:mt-0">
              {formattedDate}
              {formattedDate && (
                <>
                  <span
                    className="text-slate-400 text-xs align-middle"
                    aria-hidden="true"
                  >
                    •
                  </span>
                  <span>{readingTime} min read</span>
                </>
              )}
            </span>
          </div>
        </div>
        {/* Edge-to-edge featured image with responsive border/radius and gap below */}
        <div className="-mx-4 mb-6 sm:mx-0 flex justify-center">
          <div
            className="w-full md:max-w-4xl rounded-none border-none md:rounded-xl md:border md:border-white dark:md:border-white/30
                       shadow-none md:shadow-md md:shadow-black/20 dark:md:shadow-black/60"
            style={{ aspectRatio: "16/9", position: "relative" }}
          >
            {/* Skeleton placeholder */}
            {!imgLoaded && (
              <div className="absolute inset-0 w-full h-full rounded-none md:rounded-xl bg-slate-200 dark:bg-slate-700 animate-pulse" />
            )}
            <img
              ref={imgRef}
              src={FEATURED_IMAGE}
              alt={article.title}
              className="w-full h-full rounded-none md:rounded-xl object-cover shadow-none md:shadow-md md:shadow-black/20 dark:md:shadow-black/60 border-none md:border md:border-white dark:md:border-white/30"
              style={{ aspectRatio: "16/9" }}
              loading="lazy"
              onLoad={() => setImgLoaded(true)}
            />
          </div>
        </div>
      </section>

      {/* Article content only */}
      <section className="pb-16 pt-0">
        <div className="max-w-4xl mx-auto">
          {parsedContent ? (
            <div className={PROSE_CLASSNAME}>{parsedContent}</div>
          ) : (
            <div
              className={PROSE_CLASSNAME}
              dangerouslySetInnerHTML={{ __html: article.content_html }}
            />
          )}
        </div>
      </section>

      {/* FAQ */}
      {article.faq_items && article.faq_items.length > 0 && (
        <FAQ
          items={article.faq_items}
          title={article.faq_title ?? "Frequently Asked Questions"}
          className="pb-16"
        />
      )}

      {/* Call to Action */}
      <CallToAction
        heading={article.cta_heading ?? undefined}
        subheading={article.cta_subheading ?? undefined}
      />

      {/* Share Row */}
      <div className="mt-8 mb-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col items-center">
            <span className="mb-2 text-xs text-slate-400 tracking-wide uppercase">
              Share
            </span>
            <div className="flex gap-2 sm:gap-4">
              {/* Copy Link Button */}
              <button
                onClick={() => {
                  if (typeof navigator !== "undefined" && navigator.clipboard) {
                    navigator.clipboard.writeText(shareUrl);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1200);
                  }
                }}
                aria-label="Copy link"
                title="Copy link"
                className="transition-all duration-200 rounded-lg p-2 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/40"
              >
                <span className="block relative w-7 h-7">
                  {/* Fade between icons */}
                  <LinkIcon
                    className={`absolute inset-0 w-7 h-7 transition-opacity duration-200 ${copied ? "opacity-0" : "opacity-100"}`}
                  />
                  <Check
                    className={`absolute inset-0 w-7 h-7 text-green-600 dark:text-green-400 transition-opacity duration-200 ${copied ? "opacity-100" : "opacity-0"}`}
                  />
                </span>
              </button>
              {/* LinkedIn */}
              <a
                href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on LinkedIn"
                title="Share on LinkedIn"
                className="transition-all duration-200 rounded-lg p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0077b5]/30"
              >
                <LinkedinIcon className="w-7 h-7 hover:text-[#0077b5]" />
              </a>
              {/* X (Twitter) */}
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on X"
                title="Share on X"
                className="transition-all duration-200 rounded-lg p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/20"
              >
                <SiX className="w-7 h-7 hover:text-[#000000] dark:hover:text-white" />
              </a>
              {/* YC (Hacker News) */}
              <a
                href={`https://news.ycombinator.com/submitlink?u=${encodeURIComponent(shareUrl)}&t=${encodeURIComponent(shareTitle)}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on Hacker News"
                title="Share on Hacker News"
                className="transition-all duration-200 rounded-lg p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ff6600]/20"
              >
                <SiYcombinator className="w-7 h-7 hover:text-[#ff6600]" />
              </a>
              {/* Reddit */}
              <a
                href={`https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on Reddit"
                title="Share on Reddit"
                className="transition-all duration-200 rounded-lg p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ff4500]/20"
              >
                <SiReddit className="w-7 h-7 hover:text-[#ff4500]" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
